/**
 * The publishing plan as a calendar. Month by default, week when you need the
 * hour.
 *
 * The table answers "what is in the plan", the board "what state is it in";
 * this answers the question a plan is actually built to answer — what goes out
 * when, and where the empty weeks are. An empty Thursday is a finding here in a
 * way it can never be in a list.
 *
 * ## Undated posts are listed, not dropped
 *
 * An idea with no date is the normal state of a new post (decision 20), and a
 * month grid has nowhere to draw one. Hiding them would make the calendar
 * quietly disagree with the count in the header, so they sit under the grid as
 * chips that open the same editor.
 */
import { useMemo, useState } from 'react'
import { CalendarGrid, type CalendarSpan } from '@/shared/components/CalendarGrid'
import { todayIso } from '@/shared/util/dates'
import { postStatusStyle, type Post } from '@/types/posts'
import { ChannelDots } from './ChannelDots'
import './PostCalendar.css'

interface Props {
  posts: Post[]
  onSelect: (id: string) => void
  /** Said in the caller's words, because only it knows why the plan is empty. */
  emptyNote: string
}

export function PostCalendar({ posts, onSelect, emptyNote }: Props) {
  const today = todayIso()
  /**
   * The month, because a publishing plan is planned in months — the paid
   * budget, the campaign and the content calendar all run to one. The week is
   * for the days before a launch, when several posts land on one afternoon and
   * the times matter.
   */
  const [span, setSpan] = useState<CalendarSpan>('month')
  const [anchor, setAnchor] = useState(today)

  const { byDate, undated } = useMemo(() => {
    const map = new Map<string, Post[]>()
    const none: Post[] = []
    for (const p of posts) {
      if (!p.date) {
        none.push(p)
        continue
      }
      const list = map.get(p.date)
      if (list) list.push(p)
      else map.set(p.date, [p])
    }
    // Within a day, by the hour it goes out. A post with no time goes first:
    // it is scheduled for the day rather than for a moment in it.
    for (const list of map.values()) list.sort((a, b) => (a.time || '').localeCompare(b.time || ''))
    return { byDate: map, undated: none }
  }, [posts])

  return (
    <CalendarGrid
      span={span}
      onSpanChange={setSpan}
      anchor={anchor}
      onAnchorChange={setAnchor}
      today={today}
      footer={
        <>
          {undated.length > 0 && (
            <div className="pubcal__undated">
              <div className="card-label">
                No date yet ({undated.length})
              </div>
              <div className="pubcal__undatedlist">
                {undated.map((p) => (
                  <button className="chip" key={p.id} onClick={() => onSelect(p.id)}>
                    {p.subject || p.type}
                  </button>
                ))}
              </div>
            </div>
          )}
          {posts.length === 0 && <div className="empty-note pubcal__empty">{emptyNote}</div>}
        </>
      }
    >
      {(cell) => {
        const items = byDate.get(cell.key)
        if (!items) return null
        return (
          <div className="pubcal__items">
            {items.map((p) => {
              const st = postStatusStyle(p.status)
              return (
                <button
                  className="pubcal__item"
                  key={p.id}
                  onClick={() => onSelect(p.id)}
                  style={{ borderLeftColor: st.color, background: st.bg }}
                  title={[
                    p.subject || p.type,
                    p.time,
                    st.label,
                    p.channels.join(', ') || 'no channel yet',
                    p.owner,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                >
                  {/* Filled dots only: a day cell is too narrow for the six
                      aligned slots the table column exists to give. */}
                  <ChannelDots channels={p.channels} filledOnly />
                  <span className="pubcal__what">
                    {p.time && <b className="pubcal__time">{p.time}</b>}
                    {p.subject || p.type}
                  </span>
                </button>
              )
            })}
          </div>
        )
      }}
    </CalendarGrid>
  )
}
